// @catalog F.006
// Primitivo compartido KPICard — usado por KPICards de /ventas y /margenes
// (Bloque 3 plan dashboard_ux_ventas_margenes 2026-05-21).
import type { ReactNode } from "react";
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { ResponsiveContainer, LineChart, Line } from "recharts";

export type DeltaSign = "positive" | "negative" | "neutral";

interface SparklinePoint {
  value: number;
}

interface KPICardProps {
  title: string;
  value: string;
  // Delta porcentual ya calculado por el padre (puede ser null si no hay
  // base comparable — no inventamos 100% como hacía la versión legacy).
  deltaPercent?: number | null | undefined;
  deltaLabel?: string | undefined;
  helperText?: string | undefined;
  icon?: ReactNode;
  loading?: boolean | undefined;
  sparklineData?: SparklinePoint[] | undefined;
  // Forzar el signo del delta — útil cuando "vs anterior" siempre es comparativo.
  forceSign?: DeltaSign | undefined;
  // Tooltip opcional sobre el card (P.001 + tooltips del Bloque 13 + actual).
  tooltip?: string | undefined;
  // Si true, render con border-left de 3px en var(--accent) — usado para
  // resaltar el KPI primario del strip ejecutivo (plan dashboard_executive).
  accent?: boolean | undefined;
  // Slot custom para reemplazar el sparkline (ej. ProjectionBar en KPI
  // "Proyección cierre"). Si se provee, ignora sparklineData.
  visualSlot?: ReactNode;
  // Pill secundario opcional (ej. "% del mes", chips numéricos).
  pill?: { label: string; tone?: "neutral" | "pos" | "neg" | "warn" } | undefined;
}

function pickSign(
  delta: number | null | undefined,
  forced?: DeltaSign,
): DeltaSign {
  if (forced) return forced;
  if (delta === null || delta === undefined) return "neutral";
  if (delta > 0) return "positive";
  if (delta < 0) return "negative";
  return "neutral";
}

function pickIcon(sign: DeltaSign): ReactNode {
  if (sign === "positive") {
    return <TrendingUpIcon fontSize="small" aria-hidden="true" />;
  }
  if (sign === "negative") {
    return <TrendingDownIcon fontSize="small" aria-hidden="true" />;
  }
  return <TrendingFlatIcon fontSize="small" aria-hidden="true" />;
}

function pickColor(sign: DeltaSign): string {
  if (sign === "positive") return "chart.growthPositive";
  if (sign === "negative") return "chart.growthNegative";
  return "text.secondary";
}

function formatDelta(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return "Sin base";
  const sign = delta > 0 ? "+" : "";
  return `${sign}${delta.toFixed(1)}%`;
}

export function KPICard(props: KPICardProps): JSX.Element {
  const {
    title,
    value,
    deltaPercent,
    deltaLabel,
    helperText,
    icon,
    loading,
    sparklineData,
    forceSign,
    tooltip,
    accent = false,
    visualSlot,
    pill,
  } = props;

  const theme = useTheme();
  const sign = pickSign(deltaPercent, forceSign);
  const trendIcon = pickIcon(sign);
  const trendColor = pickColor(sign);
  const sparklineStroke = theme.palette.chart.primaryLine;

  const card = (
    <Card
      role="article"
      aria-label={title}
      sx={{
        flex: 1,
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "var(--surface)",
        border: "1px solid var(--border)",
        boxShadow: "none",
        ...(accent && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            backgroundColor: "var(--accent)",
          },
        }),
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: { xs: 2, md: 2.5 },
          "&:last-child": { pb: { xs: 2, md: 2.5 } },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--ink-3)",
                opacity: 0.85,
                "& svg": { fontSize: 14 },
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            variant="body2"
            sx={{
              color: "var(--ink-3)",
              fontSize: "12px",
              fontWeight: 500,
            }}
            noWrap
          >
            {title}
          </Typography>
        </Stack>

        {loading ? (
          <Skeleton variant="text" width={140} height={48} />
        ) : (
          <Typography
            component="div"
            sx={{
              fontSize: "28px",
              fontWeight: 600,
              letterSpacing: "-0.025em",
              lineHeight: 1.1,
              color: "var(--ink-1)",
              fontVariantNumeric: "tabular-nums",
              wordBreak: "break-word",
              marginTop: "6px",
            }}
          >
            {value}
          </Typography>
        )}

        {(deltaPercent !== undefined || pill) && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ marginTop: "8px", flexWrap: "wrap", rowGap: "4px" }}
          >
            {deltaPercent !== undefined && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "3px",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "11.5px",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  color: trendColor,
                  backgroundColor:
                    sign === "positive"
                      ? "var(--pos-soft)"
                      : sign === "negative"
                        ? "var(--neg-soft)"
                        : "var(--bg-2)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {trendIcon}
                {formatDelta(deltaPercent)}
              </Box>
            )}
            {pill && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "11.5px",
                  fontWeight: 600,
                  color:
                    pill.tone === "pos"
                      ? "var(--pos)"
                      : pill.tone === "neg"
                        ? "var(--neg)"
                        : pill.tone === "warn"
                          ? "var(--warn)"
                          : "var(--ink-3)",
                  backgroundColor:
                    pill.tone === "pos"
                      ? "var(--pos-soft)"
                      : pill.tone === "neg"
                        ? "var(--neg-soft)"
                        : pill.tone === "warn"
                          ? "var(--warn-soft)"
                          : "var(--bg-2)",
                }}
              >
                {pill.label}
              </Box>
            )}
            {deltaLabel && (
              <Typography
                variant="caption"
                sx={{ color: "var(--ink-3)", fontSize: "12px" }}
              >
                {deltaLabel}
              </Typography>
            )}
          </Stack>
        )}

        {helperText && (
          <Typography
            variant="caption"
            sx={{ mt: 0.5, color: "var(--ink-3)", fontSize: "12px" }}
          >
            {helperText}
          </Typography>
        )}

        {visualSlot ? (
          <Box sx={{ mt: "auto" }}>{visualSlot}</Box>
        ) : (
          sparklineData &&
          sparklineData.length > 1 && (
            <Box sx={{ height: 40, mt: "auto", pt: "14px" }} aria-hidden="true">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparklineData}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={sparklineStroke}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )
        )}
      </CardContent>
    </Card>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        <Box sx={{ height: "100%", flex: 1, minWidth: 0 }}>{card}</Box>
      </Tooltip>
    );
  }
  return card;
}
